// frontend/src/components/CourierDispatchReport.js

import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Alert,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { getCourierDispatchSummary } from '../services/api';
import { useSnackbar } from '../contexts/SnackbarContext';
import moment from 'moment';
import CustomTable from './CustomTable'; // Import the CustomTable component

const colorPalette = [
    '#037ec7', // Evri
    '#20c997', // Parcelforce
    '#dc3545', // Royal Mail
    '#ffc107', // DHL
    '#6f42c1', // DPD
    '#007bff', // Fedex
    '#28a745', // UPS
    // Add more colors as needed
];

const CourierDispatchReport = ({
    granularity,
    startDate,
    endDate,
    selectedClients = [],
    refreshKey = 0,
}) => {
    const [dispatchData, setDispatchData] = useState([]);
    const [aggregatedData, setAggregatedData] = useState([]);
    const [couriers, setCouriers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { showSnackbar } = useSnackbar();

    const fetchDispatchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const data = await getCourierDispatchSummary(
                granularity,
                startDate,
                endDate,
                selectedClients
            );
            setDispatchData(data);
        } catch (error) {
            setError('Unable to load courier dispatch data for the selected filters.');
            showSnackbar('Failed to fetch courier dispatch data.', 'error');
            console.error('Fetch Dispatch Data Error:', error);
        } finally {
            setLoading(false);
        }
    }, [endDate, granularity, selectedClients, showSnackbar, startDate]);

    useEffect(() => {
        fetchDispatchData();
    }, [fetchDispatchData, refreshKey]);

    useEffect(() => {
        if (dispatchData.length === 0) {
            setAggregatedData([]);
            setCouriers([]);
            return;
        }

        // Extract unique couriers
        const uniqueCouriers = [...new Set(dispatchData.map(item => item.Courier))].sort();
        setCouriers(uniqueCouriers);

        // Aggregate data by Period
        const aggregationMap = {};

        dispatchData.forEach(item => {
            const period = granularity === 'Month'
                ? moment({ year: item.Year, month: item.Period - 1 }).format('YYYY MMM')
                : granularity === 'Week'
                    ? `Week Starting ${moment(item.Period).format('YYYY-MM-DD')}`
                    : moment(item.Period).format('YYYY-MM-DD');

            if (!aggregationMap[period]) {
                aggregationMap[period] = { Period: period };
                uniqueCouriers.forEach(courier => {
                    aggregationMap[period][courier] = 0;
                });
            }

            aggregationMap[period][item.Courier] += item.OrdersSent;
        });

        const aggregatedArray = Object.values(aggregationMap).sort((a, b) => {
            if (granularity === 'Day' || granularity === 'Week') {
                return moment(a.Period, granularity === 'Week' ? 'Week Starting YYYY-MM-DD' : 'YYYY-MM-DD').diff(moment(b.Period, granularity === 'Week' ? 'Week Starting YYYY-MM-DD' : 'YYYY-MM-DD'));
            } else { // Month
                return moment(a.Period, 'YYYY MMM').diff(moment(b.Period, 'YYYY MMM'));
            }
        });

        setAggregatedData(aggregatedArray);
    }, [dispatchData, granularity]);

    // Define table columns
    const columns = [
        {
            id: 'Period',
            label: 'Period',
            minWidth: 150,
            align: 'left',
            sortable: true,
        },
        ...couriers.map((courier, index) => ({
            id: courier,
            label: courier,
            minWidth: 120,
            align: 'right',
            sortable: false,
        })),
    ];

    // State for pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Assign colors to couriers
    const courierColors = useMemo(() => {
        return couriers.reduce((acc, courier, index) => {
            acc[courier] = colorPalette[index % colorPalette.length];
            return acc;
        }, {});
    }, [couriers]);

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 900 }}>
                    Courier Dispatch Report
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Carrier mix and dispatched order volume for the shared management filters.
                </Typography>
            </Box>
                {loading ? (
                    <Box sx={{ minHeight: 320, display: 'grid', placeItems: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : aggregatedData.length === 0 ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>No courier dispatch data</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Adjust the date or client filters and refresh the report.
                        </Typography>
                    </Paper>
                ) : (
                    <>
                        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2, borderRadius: 2.5 }}>
                            <Typography variant="h6" sx={{ mb: 1, fontWeight: 800 }}>
                                Orders Sent per Courier
                            </Typography>
                            <Box sx={{ width: '100%', height: { xs: 320, md: 410 } }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart
                                        data={aggregatedData}
                                        margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="Period" minTickGap={25} />
                                        <YAxis allowDecimals={false} />
                                        <Tooltip />
                                        <Legend />
                                        {couriers.map((courier) => (
                                            <Bar
                                                key={courier}
                                                dataKey={courier}
                                                stackId="a"
                                                fill={courierColors[courier]}
                                            />
                                        ))}
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </Paper>

                        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 2.5, overflow: 'hidden' }}>
                            <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 800 }}>
                                Courier Dispatch Summary Table
                            </Typography>
                            <CustomTable
                                columns={columns}
                                data={aggregatedData.map(row => ({
                                    Period: row.Period,
                                    ...couriers.reduce((acc, courier) => {
                                        acc[courier] = row[courier] || 0;
                                        return acc;
                                    }, {}),
                                }))}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                handleChangePage={handleChangePage}
                                handleChangeRowsPerPage={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </>
                )}
        </Box>
    )
};

export default CourierDispatchReport;
